import { BREADCRUMB_PAGE } from "components/Breadcrumb/constants";
import { MainLayoutContainer } from "components/Layout";
import { FC } from "react";

import { ErrorMessage, FormikProvider, useFormik } from "formik";
import { getValidationSchema } from "./config";

import {
  Box,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { StyledFormicError, VisuallyHiddenInput } from "./styles";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import { green } from "@mui/material/colors";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { ORG_TYPE, Organization } from "types/organization";
import { Img } from "components/Img";
import { ListWithEntitySelector } from "components/ListWithEntitySelector";
import { ROLES, User } from "types/user";
import { useCreateOrEditOrganization } from "./useCreateOrEditOrganization";
import { Site } from "types/site";
import { CustomLabels } from "components/CustomLabel/CustomLabels";
import { Edit } from "@mui/icons-material";

const ORG_TYPES = [
  { value: ORG_TYPE.EDUCATIONAL, label: "Educational" },
  { value: ORG_TYPE.CLINIC, label: "Clinic" },
];

export const CreateOrEditOrganization: FC = () => {
  const {
    onUploadFile,
    uploading,
    img,
    errorImage,
    isEditMode,
    users,
    allSites,
    sites,
    directors,
    setSites,
    setDirectors,
    managers,
    setManagers,
    coaches,
    setCoaches,
    loading,
    handleCreateOrEditOrganization,
    handleEditOrganization,
    hasProgram,
    handleAddProgram,
    initialValues,
    organizationId,
    isClinical,
    customLabels,
    fetchCustomLabels,
  } = useCreateOrEditOrganization();

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: getValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const reqValues = {
        type: values.organizationType,
        name: values.organizationName,
        primary_contact_name: values.primaryContactName,
        primary_contact_email: values.primaryContactEmail,
        secondary_contact_name: values.secondaryContactName,
        secondary_contact_email: values.secondaryContactEmail,
        logo_url: img,
      };
      if (isEditMode) {
        handleEditOrganization(reqValues as Organization).then();
      } else {
        handleCreateOrEditOrganization(reqValues as Organization).then();
      }
    },
  });

  return (
    <MainLayoutContainer
      title={`${isEditMode ? "Edit" : "Create"} Organization`}
      breadcrumb={BREADCRUMB_PAGE.ORGANIZATION}
    >
      <Box sx={{ maxWidth: 960 }}>
        {isEditMode && (
          <Box mb={2}>
            {hasProgram ? (
              <Alert severity="success">This organization has a program.</Alert>
            ) : (
              <Alert
                severity="info"
                action={
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={handleAddProgram}
                  >
                    Add Program
                  </Button>
                }
              >
                No program configured.
              </Alert>
            )}
          </Box>
        )}
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ mt: 1 }}>
              <Box>
                <FormControl variant="standard" fullWidth margin="dense">
                  <InputLabel id="organizationTypeLabel">
                    Organization Type
                  </InputLabel>
                  <Select
                    labelId="organizationTypeLabel"
                    id="organizationType"
                    name="organizationType"
                    value={formik?.values?.organizationType}
                    onChange={formik.handleChange}
                    label="Organization Type"
                  >
                    {ORG_TYPES.map((type) => (
                      <MenuItem value={type.value} key={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <ErrorMessage
                  name="organizationType"
                  component={StyledFormicError}
                />
              </Box>

              <Box>
                <TextField
                  variant="standard"
                  onChange={formik.handleChange}
                  margin="dense"
                  fullWidth
                  name="organizationName"
                  label="Organization Name"
                  type="text"
                  id="organizationName"
                  value={formik?.values?.organizationName}
                />
                <ErrorMessage
                  name="organizationName"
                  component={StyledFormicError}
                />
              </Box>

              <Box>
                <TextField
                  variant="standard"
                  onChange={formik.handleChange}
                  margin="dense"
                  fullWidth
                  name="primaryContactName"
                  label="Primary Contact Name"
                  type="text"
                  id="primaryContactName"
                  value={formik?.values?.primaryContactName}
                />
                <ErrorMessage
                  name="primaryContactName"
                  component={StyledFormicError}
                />
              </Box>

              <Box>
                <TextField
                  variant="standard"
                  onChange={formik.handleChange}
                  margin="dense"
                  fullWidth
                  name="primaryContactEmail"
                  label="Primary Contact Email"
                  type="text"
                  id="primaryContactEmail"
                  value={formik?.values?.primaryContactEmail}
                />
                <ErrorMessage
                  name="primaryContactEmail"
                  component={StyledFormicError}
                />
              </Box>

              <Box>
                <TextField
                  variant="standard"
                  onChange={formik.handleChange}
                  margin="dense"
                  fullWidth
                  name="secondaryContactName"
                  label="Secondary Contact Name"
                  type="text"
                  id="secondaryContactName"
                  value={formik?.values?.secondaryContactName}
                />
                <ErrorMessage
                  name="secondaryContactName"
                  component={StyledFormicError}
                />
              </Box>

              <Box>
                <TextField
                  variant="standard"
                  onChange={formik.handleChange}
                  margin="dense"
                  fullWidth
                  name="secondaryContactEmail"
                  label="Secondary Contact Email"
                  type="text"
                  id="secondaryContactEmail"
                  value={formik?.values?.secondaryContactEmail}
                />
                <ErrorMessage
                  name="secondaryContactEmail"
                  component={StyledFormicError}
                />
              </Box>

              <Box mt={2}>
                <InputLabel>Add organization logo (optional)</InputLabel>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                  py={2}
                >
                  <Box sx={{ position: "relative" }}>
                    <Button
                      disabled={uploading}
                      component="label"
                      role={undefined}
                      variant="outlined"
                      tabIndex={-1}
                      startIcon={<CloudUploadIcon />}
                    >
                      Upload files
                      <VisuallyHiddenInput
                        type="file"
                        onChange={onUploadFile}
                      />
                    </Button>
                    {errorImage !== "" && (
                      <Box mt={1} sx={{ color: "red" }}>
                        {errorImage}
                      </Box>
                    )}

                    {uploading && (
                      <CircularProgress
                        size={30}
                        sx={{
                          color: green[500],
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          marginTop: "-12px",
                          marginLeft: "-12px",
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ position: "relative" }}>
                    <Img src={img} width={80} height={80} />
                    {uploading && (
                      <CircularProgress
                        size={30}
                        sx={{
                          color: green[500],
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          marginTop: "-12px",
                          marginLeft: "-12px",
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>

              <Typography variant="h6" sx={{ mt: 2 }}>
                Sites
              </Typography>

              <Box>
                <Box>
                  <ListWithEntitySelector
                    elements={sites}
                    onChangeElements={(sites: Site[]) => setSites(sites)}
                    idField="site_id"
                    entityName="Site"
                    entityList={allSites}
                  />
                </Box>
              </Box>

              <Box>
                <Divider sx={{ my: 2 }} />

                <CustomLabels
                  organizationId={organizationId}
                  customLabels={customLabels}
                  onAddCustomLabel={() => {
                    if (organizationId) {
                      fetchCustomLabels();
                    }
                  }}
                  displayHiddenButton={false}
                  displayEditButton={true}
                  isClinical={isClinical}
                />
              </Box>

              <Typography variant="h6" sx={{ mt: 2 }}>
                User Management
              </Typography>

              <Box>
                <Typography variant="body1" fontWeight="600" sx={{ mt: 2 }}>
                  Directors
                </Typography>
                <Box>
                  <ListWithEntitySelector
                    elements={directors}
                    onChangeElements={(users: User[]) => setDirectors(users)}
                    idField="user_id"
                    entityName="Director"
                    entityList={users.filter(
                      (user) => user.role === ROLES.DIRECTOR,
                    )}
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="body1" fontWeight="600" sx={{ mt: 2 }}>
                  Managers
                </Typography>
                <Box>
                  <ListWithEntitySelector
                    elements={managers}
                    onChangeElements={(users: User[]) => setManagers(users)}
                    idField="user_id"
                    entityName="Manager"
                    entityList={users.filter(
                      (user) => user.role === ROLES.MANAGER,
                    )}
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="body1" fontWeight="600" sx={{ mt: 2 }}>
                  Coaches
                </Typography>
                <Box>
                  <ListWithEntitySelector
                    elements={coaches}
                    onChangeElements={(users: User[]) => setCoaches(users)}
                    idField="user_id"
                    entityName="Coach"
                    entityList={users.filter(
                      (user) => user.role === ROLES.COACH,
                    )}
                  />
                </Box>
              </Box>

              <Grid sx={{ mt: 5, display: "flex", justifyContent: "flex-end" }}>
                <Box mr={2}>
                  <Button variant="outlined" color="primary">
                    Cancel
                  </Button>
                </Box>
                <Box>
                  <Button
                    disabled={loading}
                    type="submit"
                    variant="contained"
                    color="primary"
                  >
                    Submit
                  </Button>
                </Box>
                {loading && (
                  <CircularProgress
                    size={30}
                    sx={{
                      color: green[500],
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                )}
              </Grid>
            </Box>
          </form>
        </FormikProvider>
      </Box>
    </MainLayoutContainer>
  );
};
